import { useState } from "react";
import { PayloadType, ParseAlgorithmDto, SchemaFieldItem, DeviceTypeId } from "../../../types";
import {
  getPayloadData,
  parsePayload,
  parsePayloadWithValue,
  extractJsonSchema,
  extractJsonSchemaWithValue,
  createPayloadData,
  createKeyValueTable,
  updatePayloadData,
  updateKeyValueTable,
  getKeyValueTablesRaw,
  getGroupTablesByKeyValueIds,
} from "../../../services/payloadDataService";
import { buildUuidIndexMap, hydrateBindingData, normalizeParseTable, normalizeBindingData, buildUpdatePayload } from "../../../utils/payloadNormalizer";

export const usePayloadParser = (selectedParserConfig: number, payloadTypeId: number, deviceTypeId: number) => {
  const [payloadText, setPayloadText] = useState("");
  const [payloadData, setPayloadData] = useState<PayloadType[]>([]);
  const [schemaItems, setSchemaItems] = useState<SchemaFieldItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [isPayloadEdited, setIsPayloadEdited] = useState(false);
  const [isExistingData, setIsExistingData] = useState(false);

  const mapSchemaItems = (data: any[]): SchemaFieldItem[] =>
    data.map(item =>
      typeof item === "string"
        ? { key: item, isArray: false, isDeviceId: false }
        : { key: item.key ?? "", isArray: item.isArray ?? false, isDeviceId: item.isDeviceId ?? false }
    );

  const fetchDataAndParse = async () => {
    try {
      setLoading(true);
      const res = await getPayloadData(selectedParserConfig);
      if (!res.data.success) throw new Error(res.data.message);

      if (res.data.data?.payload) {
        const payload = res.data.data.payload;
        setPayloadText(payload);
        await handlePayloadUploadWithValue(payload);
        setIsExistingData(true);
      }
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handlePayloadUploadWithValue = async (payload?: string) => {
    const payloadToParse = payload ?? payloadText;
    if (!payloadToParse?.trim()) throw new Error("Veri boş olamaz");

    try {
      setLoading(true);
      setPayloadData([]);

      const response = deviceTypeId === DeviceTypeId.Gateway
        ? await extractJsonSchemaWithValue({ payload: payloadToParse, parserConfigId: selectedParserConfig })
        : await parsePayloadWithValue({ payload: payloadToParse, parserConfigId: selectedParserConfig, payloadTypeId });

      if (!response.data.success) throw new Error(response.data.message);

      if (deviceTypeId === DeviceTypeId.Gateway) {
        setSchemaItems(mapSchemaItems(response.data.data as any[]));
      } else {
        setPayloadData(
          response.data.data.map((item: PayloadType) => ({
            ...item,
            parseTable: item.parseTable ?? [],
            bindingData: item.bindingData ?? [],
          }))
        );
      }
      setIsPayloadEdited(false);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handlePayloadUpload = async (payload?: string) => {
    setIsExistingData(false);
    const payloadToParse = payload ?? payloadText;
    if (!payloadToParse?.trim()) throw new Error("Veri boş olamaz");

    try {
      setLoading(true);
      setPayloadData([]);

      const response = deviceTypeId === DeviceTypeId.Gateway
        ? await extractJsonSchema({ payload: payloadToParse })
        : await parsePayload({ payload: payloadToParse, payloadTypeId });

      if (!response.data.success) throw new Error(response.data.message);

      if (deviceTypeId === DeviceTypeId.Gateway) {
        setSchemaItems(mapSchemaItems(response.data.data as any[]));
      } else {
        setPayloadData(
          response.data.data.map((item: PayloadType) => {
            const uuidIndexMap = buildUuidIndexMap(item.parseTable ?? []);
            return {
              ...item,
              parseTable: item.parseTable ?? [],
              bindingData: hydrateBindingData(item.bindingData ?? [], uuidIndexMap),
            };
          })
        );
      }
      setIsPayloadEdited(false);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAll = async (): Promise<string> => {
    try {
      setLoading(true);
      if (!payloadText) throw new Error("Veri boş olamaz");

      const payloadBody = { payload: payloadText, parserConfigId: selectedParserConfig, payloadTypeId };
      const payloadResponse = isExistingData
        ? await updatePayloadData(payloadBody)
        : await createPayloadData(payloadBody);
      if (!payloadResponse.data.success) throw new Error(payloadResponse.data.message);

      const saveKvTable = isExistingData ? updateKeyValueTable : createKeyValueTable;

      if (deviceTypeId === DeviceTypeId.Gateway) {
        if (!schemaItems.some(x => x.isDeviceId)) throw new Error("Bir DeviceId alanı seçilmelidir.");
        if (!schemaItems.some(x => x.isArray)) throw new Error("Bir Array alanı seçilmelidir.");

        const schemaPayload = schemaItems
          .filter(x => x.isArray || x.isDeviceId)
          .map(item => ({
            key: item.key,
            isRawData: false,
            rawDataTypeId: 0,
            parserConfigId: selectedParserConfig,
            description: "",
            isDeviceId: item.isDeviceId,
            isArray: item.isArray,
            parseTable: [] as ParseAlgorithmDto[],
            bindingData: [],
          }));

        const kvResponse = await saveKvTable(schemaPayload);
        if (!kvResponse.data.success) throw new Error(kvResponse.data.message);

        const kvDataArray = Array.isArray(kvResponse.data.data) ? kvResponse.data.data : [];
        setSchemaItems(prev =>
          prev.map(item => {
            const updated = kvDataArray.find((r: any) => r.key === item.key);
            if (!updated) return item;
            return { ...item, isArray: updated.isArray ?? item.isArray, isDeviceId: updated.isDeviceId ?? item.isDeviceId };
          })
        );
      } else {
        if (payloadData.filter(x => x.dbRecord).some(p => p.isRawData && (p.parseTable ?? []).length === 0)) {
          throw new Error("Parse edilen alanlar için kural tanımlanmalıdır.");
        }

        let payload: any[];
        if (isExistingData) {
          const kvRawRes = await getKeyValueTablesRaw(selectedParserConfig);
          const kvRaw = Array.isArray(kvRawRes.data?.data) ? kvRawRes.data.data : [];
          const kvIds: number[] = kvRaw.map((k: any) => k.id).filter(Boolean);
          const groupRaw = kvIds.length > 0
            ? (await getGroupTablesByKeyValueIds(kvIds)).data?.data ?? []
            : [];
          payload = buildUpdatePayload(payloadData, kvRaw, groupRaw, selectedParserConfig);
        } else {
          payload = payloadData
            .filter(x => x.dbRecord)
            .map(item => ({
              key: item.key,
              isRawData: item.isRawData,
              rawDataTypeId: item.rawDataTypeId ?? 0,
              parserConfigId: selectedParserConfig,
              description: item.description ?? "",
              isDeviceId: item.isDeviceId ?? false,
              isArrayExpand: item.isArrayExpand ?? false,
              parseTable: item.isRawData ? normalizeParseTable(item.parseTable ?? []) : [],
              bindingData: item.isRawData ? normalizeBindingData(item.bindingData ?? []) : [],
            }));
        }

        const kvResponse = await saveKvTable(payload);
        if (!kvResponse.data.success) throw new Error(kvResponse.data.message);

        const kvDataArray = Array.isArray(kvResponse.data.data) ? kvResponse.data.data : [];
        setPayloadData(prev =>
          prev.map(item => {
            const updatedItem = kvDataArray.find((r: any) => r.key === item.key);
            if (!updatedItem) return item;
            return {
              ...item,
              parseTable: updatedItem.parseTable ?? item.parseTable,
              bindingData: updatedItem.bindingData ?? item.bindingData,
            };
          })
        );
      }

      setIsExistingData(true);
      setIsPayloadEdited(false);
      return "Tüm veriler başarıyla kaydedildi";
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const resetState = () => {
    setPayloadData([]);
    setSchemaItems([]);
    setPayloadText("");
    setIsPayloadEdited(false);
    setIsExistingData(false);
  };

  const handlePayloadChange = (value: string) => {
    setPayloadText(value);
    setIsPayloadEdited(true);
  };

  const handleRowUpdate = (key: string, updates: Partial<PayloadType>) => {
    setPayloadData(prev => prev.map(x => (x.key === key ? { ...x, ...updates } : x)));
  };

  const handleIsDeviceIdChange = (key: string, value: boolean) => {
    setPayloadData(prev =>
      prev.map(x => (x.key === key ? { ...x, isDeviceId: value } : { ...x, isDeviceId: false }))
    );
  };

  const handleArrayExpandRowUpdate = (normalizedKey: string, updates: Partial<PayloadType>) => {
    setPayloadData(prev => {
      const existingIndex = prev.findIndex(x => x.key === normalizedKey && x.isArrayExpand === true);
      const existing = existingIndex >= 0 ? prev[existingIndex] : null;
      const merged = { ...existing, ...updates };
      const isBecomingActive = !!(merged.dbRecord || merged.isRawData);
      const shouldRemove = !merged.dbRecord && !merged.isRawData;

      const normalizeK = (k: string) => k.replace(/\[\d+\]/g, "[*]");
      const isArrayK = (k: string) => /\[\d+\]/.test(k);

      let base = isBecomingActive
        ? prev.map(x =>
            !x.isArrayExpand && isArrayK(x.key) && normalizeK(x.key) === normalizedKey
              ? { ...x, dbRecord: false, isRawData: false }
              : x
          )
        : prev;

      if (existingIndex >= 0) {
        if (shouldRemove) return base.filter((_, i) => i !== existingIndex);
        return base.map((x, i) => (i === existingIndex ? { ...x, ...updates } : x));
      }

      if (!shouldRemove) {
        return [
          ...base,
          {
            key: normalizedKey,
            value: {} as object,
            isRawData: false,
            rawDataTypeId: 0,
            dbRecord: false,
            description: "",
            isArrayExpand: true,
            parseTable: [],
            bindingData: [],
            ...updates,
          },
        ];
      }

      return base;
    });
  };

  const handleParseRuleSave = (selectedItemKey: string, rules: any[], bindings: any[]) => {
    const cleanedBindings = bindings.map(b => ({
      ...b,
      bindingParseData: b.bindingParseData.map((p: any) => ({ itemId: p.itemId, order: p.order })),
    }));

    setPayloadData(prev =>
      prev.map(item =>
        item.key === selectedItemKey
          ? { ...item, parseTable: rules, bindingData: cleanedBindings }
          : item
      )
    );
  };

  return {
    payloadText,
    payloadData,
    schemaItems,
    setSchemaItems,
    loading,
    isPayloadEdited,
    isExistingData,
    fetchDataAndParse,
    handlePayloadUpload,
    handlePayloadUploadWithValue,
    handlePayloadChange,
    handleRowUpdate,
    handleArrayExpandRowUpdate,
    handleParseRuleSave,
    handleIsDeviceIdChange,
    handleSaveAll,
    resetState,
  };
};
